import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  auth, 
  googleProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile
} from '../lib/firebase';

const AuthContext = createContext({});

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Check for mock/demo authentication first
    const mockToken = localStorage.getItem('token');
    if (mockToken === 'mock-token') {
      // Simulate a premium user for demo purposes
      const mockUser = {
        uid: 'mock-user-id',
        email: 'demo@wanderloop.com',
        displayName: 'Demo Premium User',
        photoURL: null,
        isAnonymous: false,
        emailVerified: true,
        membership_tier: 'wanderloop_plus' // Premium membership for demo
      };
      
      setUser(mockUser);
      setLoading(false);
      setInitialized(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setLoading(true);
      
      if (firebaseUser) {
        const userData = {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          isAnonymous: firebaseUser.isAnonymous,
          emailVerified: firebaseUser.emailVerified,
          membership_tier: 'free' // Default to free tier
        };
        
        setUser(userData);
        
        // Auto-migrate local data on first login
        if (!initialized) {
          handleDataMigration(userData.uid);
          setInitialized(true);
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
    });

    return unsubscribe;
  }, [initialized]);

  const handleDataMigration = (userId) => {
    try {
      // Check if user has local data to migrate
      const localLists = localStorage.getItem('wanderloopLists');
      if (localLists) {
        const listsData = JSON.parse(localLists);
        const hasWalks = Object.values(listsData).some(list => list.walks.length > 0);
        
        if (hasWalks) {
          console.log('Local data found for migration:', Object.keys(listsData).length, 'lists');
          // TODO: In production, send this data to backend
          // For now, we keep it in localStorage as the source of truth
        }
      }
    } catch (error) {
      console.error('Data migration error:', error);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);

      // If this is a brand-new user, send the welcome email
      const isNewUser = result?.additionalUserInfo?.isNewUser
        ?? result?._tokenResponse?.isNewUser;
      if (isNewUser) {
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
        fetch(`${backendUrl}/api/auth/welcome-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: result.user.email,
            display_name: result.user.displayName || '',
          }),
        }).catch(() => {});
      }

      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  };

  const signInWithEmail = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  };

  const signUpWithEmail = async (email, password, displayName) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Update display name
      if (displayName) {
        await updateProfile(result.user, { displayName });
      }

      // Fire-and-forget: send branded welcome + verification emails via backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
      const payload = { email, display_name: displayName || '' };
      fetch(`${backendUrl}/api/auth/welcome-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});
      fetch(`${backendUrl}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }).catch(() => {});

      return { user: result.user, error: null };
    } catch (error) {
      return { user: null, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { error: null };
    } catch (error) {
      return { error: error.message };
    }
  };

  const updateUserMembership = (newTier) => {
    if (user) {
      setUser({
        ...user,
        membership_tier: newTier
      });
    }
  };

  const value = {
    user,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    logout,
    isAuthenticated: !!user,
    isGuest: !user,
    updateUserMembership
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};